/**
 * Role-Based Access Control Configuration
 * 
 * Defines all available roles and their capabilities.
 * This allows for easy addition of new roles in the future.
 */

export const ROLES = {
  ADMIN: 'admin',
  COURSE_MANAGER: 'course_manager',
  STUDENT: 'student',
};

/**
 * Role Definitions with Capabilities
 * Each role has a set of capabilities that define what they can do
 */
export const ROLE_DEFINITIONS = {
  [ROLES.ADMIN]: {
    label: 'Administrator',
    description: 'Full system access and management',
    capabilities: [
      'view_all_courses',
      'manage_all_courses',
      'create_courses',
      'delete_courses',
      'view_all_questions',
      'manage_all_questions',
      'create_questions',
      'delete_questions',
      'view_all_users',
      'manage_all_users',
      'manage_roles',
      'view_analytics',
      'view_admin_panel',
      'manage_settings',
    ],
    priority: 100, // Higher priority = more privileged
  },

  [ROLES.COURSE_MANAGER]: {
    label: 'Course Manager',
    description: 'Manage specific assigned courses',
    capabilities: [
      'view_admin_panel',
      'view_assigned_courses',
      'manage_assigned_courses',
      'create_questions_for_assigned_courses',
      'delete_questions_from_assigned_courses',
      'view_assigned_course_users',
      'manage_assigned_course_users',
      'view_course_analytics',
    ],
    priority: 50,
  },

  [ROLES.STUDENT]: {
    label: 'Student',
    description: 'Access to enrolled courses only',
    capabilities: [
      'view_enrolled_courses',
      'take_assessments',
      'view_progress',
      'view_own_profile',
      'update_own_profile',
    ],
    priority: 10,
  },
};

/**
 * Get role definition
 * @param {string} role - Role name
 * @returns {Object} Role definition with capabilities
 */
export function getRoleDefinition(role) {
  return ROLE_DEFINITIONS[role] || null;
}

/**
 * Get all available roles
 * @returns {Array} Array of role objects with metadata
 */
export function getAllRoles() {
  return Object.entries(ROLE_DEFINITIONS).map(([key, value]) => ({
    role: key,
    ...value,
  }));
}

/**
 * Check if a role exists
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export function isValidRole(role) {
  return Object.values(ROLES).includes(role);
}

/**
 * Get role priority (higher = more privileged)
 * @param {string} role - Role name
 * @returns {number} Priority level
 */
export function getRolePriority(role) {
  const definition = getRoleDefinition(role);
  return definition ? definition.priority : 0;
}

/**
 * Check if one role is higher priority than another
 * @param {string} role1 - First role
 * @param {string} role2 - Role to compare against
 * @returns {boolean} True if role1 has higher priority
 */
export function isHigherPriority(role1, role2) {
  return getRolePriority(role1) > getRolePriority(role2);
}
